<template>
  <div class="mall-track-page">
    <div class="mall-track-page__shell">
      <header class="mall-track-page__head">
        <p class="mall-track-page__eyebrow">{{ MALL_NAME }}</p>
        <h1>배송조회</h1>
        <p>로그인 없이 택배사와 송장번호로 배송 현황을 확인할 수 있습니다.</p>
      </header>

      <form class="mall-track-page__form" @submit.prevent="onSearch">
        <label class="mall-track-page__field">
          <span>택배사</span>
          <select v-model="courier" required>
            <option value="" disabled>선택</option>
            <option
              v-if="courier && !courierOptions.includes(courier as (typeof courierOptions)[number])"
              :value="courier"
            >
              {{ courier }}
            </option>
            <option v-for="name in courierOptions" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>
        <label class="mall-track-page__field">
          <span>송장번호</span>
          <input
            v-model.trim="invoice"
            type="text"
            inputmode="numeric"
            maxlength="40"
            required
            placeholder="송장번호 입력"
            autocomplete="off"
          />
        </label>
        <button type="submit" class="mall-track-page__submit" :disabled="loading || !canSearch">
          {{ loading ? '조회 중…' : '조회하기' }}
        </button>
      </form>

      <p v-if="errorMessage" class="mall-track-page__error">{{ errorMessage }}</p>

      <section
        v-if="tracking"
        ref="resultRef"
        class="mall-track-page__result"
        tabindex="-1"
      >
        <div class="mall-track-page__hero">
          <span class="mall-track-page__hero-icon" aria-hidden="true">
            <AppIcon icon="mdi-truck-fast-outline" :size="28" />
          </span>
          <p class="mall-track-page__hero-label">현재 상태</p>
          <strong class="mall-track-page__hero-status">
            {{ tracking.lastStatus || '조회 완료' }}
          </strong>
          <p class="mall-track-page__hero-meta">
            {{ tracking.courier || courier }}
            <span aria-hidden="true">·</span>
            {{ tracking.invoice || invoice }}
          </p>
        </div>

        <div class="mall-track-page__tools">
          <button type="button" class="mall-track-page__tool" :disabled="loading" @click="onSearch">
            <AppIcon icon="mdi-refresh" :size="18" />
            다시 조회
          </button>
          <button type="button" class="mall-track-page__tool" @click="copyInvoice">
            <AppIcon icon="mdi-content-copy" :size="18" />
            송장 복사
          </button>
          <a
            v-if="tracking.sourceUrl"
            class="mall-track-page__tool"
            :href="tracking.sourceUrl"
            target="_blank"
            rel="noopener"
          >
            <AppIcon icon="mdi-open-in-new" :size="18" />
            택배사
          </a>
        </div>

        <p v-if="copied" class="mall-track-page__note is-ok">송장번호를 복사했습니다.</p>
        <p v-if="tracking.message" class="mall-track-page__note">{{ tracking.message }}</p>

        <h2 v-if="events.length" class="mall-track-page__timeline-title">배송 진행</h2>
        <ol v-if="events.length" class="mall-track-page__list">
          <li
            v-for="(event, index) in eventsReversed"
            :key="`${event.at}-${index}`"
            :class="{ 'is-latest': index === 0 }"
          >
            <span class="mall-track-page__dot" aria-hidden="true" />
            <div class="mall-track-page__row-top">
              <strong>{{ event.status || '처리' }}</strong>
              <time>{{ formatDateTime(event.at) }}</time>
            </div>
            <p v-if="event.location" class="mall-track-page__place">{{ event.location }}</p>
            <p v-if="event.description" class="mall-track-page__desc">{{ event.description }}</p>
          </li>
        </ol>

        <div v-else-if="!loading" class="mall-track-page__empty">
          <AppIcon icon="mdi-truck-outline" :size="32" />
          <p>아직 표시할 배송일지가 없습니다.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { fetchPublicParcelTracking, type MallTracking } from '@/api/mallOrder'
import { MALL_COURIERS, isDirectCourierName } from '@/constants/mallCouriers'
import { MALL_NAME } from '@/constants/mall'
import { copyToClipboard } from '@/utils/clipboard'

const route = useRoute()
const router = useRouter()

const courierOptions = MALL_COURIERS.filter((name) => !isDirectCourierName(name))

const courier = ref('')
const invoice = ref('')
const loading = ref(false)
const copied = ref(false)
const errorMessage = ref('')
const tracking = ref<MallTracking | null>(null)
const resultRef = ref<HTMLElement | null>(null)

const canSearch = computed(() => Boolean(courier.value.trim() && invoice.value.trim()))

const events = computed(() => {
  const list = [...(tracking.value?.events || [])]
  return list.sort((a, b) => {
    const left = new Date(a.at).getTime()
    const right = new Date(b.at).getTime()
    if (Number.isNaN(left) || Number.isNaN(right)) return 0
    return left - right
  })
})

/** 최신 상태가 위로 오도록 (앱 타임라인) */
const eventsReversed = computed(() => [...events.value].reverse())

function formatDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value || '-'
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function resolveCourierOption(raw: string) {
  const name = String(raw || '').trim()
  if (!name) return ''
  const exact = courierOptions.find((item) => item === name)
  if (exact) return exact
  const matched = courierOptions.find((item) => {
    if (/CJ|대한통운/i.test(name) && /CJ|대한통운/i.test(item)) return true
    if (/우체국/i.test(name) && /우체국/i.test(item)) return true
    if (/한진/i.test(name) && /한진/i.test(item)) return true
    if (/롯데/i.test(name) && /롯데/i.test(item)) return true
    if (/로젠/i.test(name) && /로젠/i.test(item)) return true
    if (/경동/i.test(name) && /경동/i.test(item)) return true
    if (/대신/i.test(name) && /대신/i.test(item)) return true
    if (/일양/i.test(name) && /일양/i.test(item)) return true
    if (/CU/i.test(name) && /CU/i.test(item)) return true
    if (/GS|포스트박스/i.test(name) && /GS/i.test(item)) return true
    if (/천일/i.test(name) && /천일/i.test(item)) return true
    return false
  })
  return matched || name
}

function applyQuery() {
  const qCourier = String(route.query.courier || '').trim()
  const qInvoice = String(route.query.invoice || '').replace(/\s+/g, '')
  if (qCourier) courier.value = resolveCourierOption(qCourier)
  if (qInvoice) invoice.value = qInvoice
}

async function onSearch() {
  if (!canSearch.value || loading.value) return
  errorMessage.value = ''
  loading.value = true
  try {
    tracking.value = await fetchPublicParcelTracking(courier.value, invoice.value)
    await router.replace({
      query: {
        courier: courier.value.trim(),
        invoice: invoice.value.replace(/\s+/g, ''),
      },
    })
    await nextTick()
    resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch (error) {
    tracking.value = null
    errorMessage.value =
      error instanceof Error ? error.message : '배송조회를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function copyInvoice() {
  const value = tracking.value?.invoice || invoice.value
  const ok = await copyToClipboard(value)
  if (!ok) return
  copied.value = true
  window.setTimeout(() => {
    copied.value = false
  }, 1600)
}

onMounted(async () => {
  applyQuery()
  if (canSearch.value) {
    await onSearch()
  }
})
</script>

<style lang="scss" src="./MallTrackingPage.scss"></style>
